/**
 * ContentBlockWrapper — Editor-specific wrapper for content blocks from ::use bindings.
 *
 * Shows a config popover button on hover for content instances that have
 * configurable bind/with parameters. Injected via RenderEnvironment.ContentBlockWrapper.
 */

import type { ImportCachedSymbol, PluginDefinition, TemplateInputSchema, UseBinding } from "@magic-md/core";
import { useT } from "@magic-md/i18n/react";
import { Settings } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { cn } from "../../lib/utils";
import { useEditorStore } from "../../store/editor-store";
import { usePluginExecutionStore, type ImportExecutionEntry } from "../../store/plugin-execution-store";
import { usePaneRenderContext } from "../PaneContext";
import { UseBindEditor } from "../UseBindEditor";
import { UseWithEditor } from "../UseWithEditor";

const EMPTY_IMPORT_EXECUTIONS = new Map<string, ImportExecutionEntry>();

export function ContentBlockWrapper({
    definitionId,
    children,
}: {
    definitionId: string;
    children: React.ReactNode;
}) {
    const paneCtx = usePaneRenderContext();
    const [showConfig, setShowConfig] = useState(false);
    const popoverRef = useRef<HTMLDivElement>(null);

    // Find the ::use binding for this content block
    const useBinding = paneCtx.parsedDocument?.uses?.find(
        (u) => u.id === definitionId,
    );

    // If no use binding, just render children as-is
    if (!useBinding) {
        return <>{children}</>;
    }

    return (
        <ContentConfigOverlay
            useBinding={useBinding}
            definitions={paneCtx.definitions}
            showConfig={showConfig}
            setShowConfig={setShowConfig}
            popoverRef={popoverRef}
            paneCtx={paneCtx}
        >
            {children}
        </ContentConfigOverlay>
    );
}

/** Inner component that always renders (hooks-safe — no conditional returns before hooks). */
function ContentConfigOverlay({
    useBinding,
    definitions,
    showConfig,
    setShowConfig,
    popoverRef,
    paneCtx,
    children,
}: {
    useBinding: UseBinding;
    definitions: Map<string, PluginDefinition>;
    showConfig: boolean;
    setShowConfig: (v: boolean | ((prev: boolean) => boolean)) => void;
    popoverRef: React.RefObject<HTMLDivElement | null>;
    paneCtx: ReturnType<typeof usePaneRenderContext>;
    children: React.ReactNode;
}) {
    const t = useT("editor");
    const importExecutions = usePluginExecutionStore(
        useShallow((s) => {
            const activeTabId = useEditorStore.getState().activeTabId;
            if (paneCtx.tabId === activeTabId) return s.importExecutions;
            const cachedSessionId = s.tabSessionMap.get(paneCtx.tabId);
            return cachedSessionId
                ? (s.sessionExecutionCache.get(cachedSessionId)?.importExecutions ?? EMPTY_IMPORT_EXECUTIONS)
                : EMPTY_IMPORT_EXECUTIONS;
        }),
    );
    // Derive source config for UseBindEditor
    const useSourceConfig = useMemo(() => {
        const sourceDef = definitions.get(useBinding.source);
        return sourceDef?.config as Record<string, unknown> | undefined;
    }, [useBinding.source, definitions]);

    // Collect import symbols for bind key discovery
    const importSymbols = useMemo<ImportCachedSymbol[] | undefined>(() => {
        const syms: ImportCachedSymbol[] = [];
        for (const imp of paneCtx.parsedDocument?.imports ?? []) {
            if (imp.output?.symbols) syms.push(...imp.output.symbols);
        }
        for (const importExecution of importExecutions.values()) {
            if (importExecution.symbols) {
                syms.push(...(importExecution.symbols as ImportCachedSymbol[]));
            }
        }
        return syms.length > 0 ? syms : undefined;
    }, [paneCtx.parsedDocument?.imports, importExecutions]);

    // Template inputs for any templateable source used by ::use.
    const templateSource = useMemo<{ params?: string[]; inputSchema?: TemplateInputSchema } | undefined>(() => {
        const localDefinition = definitions.get(useBinding.source);
        if (localDefinition?.template && (localDefinition.params?.length || localDefinition.inputSchema)) {
            return { params: localDefinition.params, inputSchema: localDefinition.inputSchema };
        }
        const localContent = paneCtx.contentDefinitions.get(useBinding.source);
        if (localContent?.template && (localContent.params?.length || localContent.inputSchema)) {
            return { params: localContent.params, inputSchema: localContent.inputSchema };
        }
        const localFor = paneCtx.parsedDocument?.forDefinitions?.get(useBinding.source);
        if (localFor?.template && (localFor.params?.length || localFor.inputSchema)) {
            return { params: localFor.params, inputSchema: localFor.inputSchema };
        }
        const localUse = paneCtx.parsedDocument?.uses?.find(
            (binding) => binding.id === useBinding.source,
        );
        if (localUse?.template && (localUse.params?.length || localUse.inputSchema)) {
            return { params: localUse.params, inputSchema: localUse.inputSchema };
        }
        if (importSymbols) {
            const sym = importSymbols.find(
                (s) =>
                    s.qualifiedId === useBinding.source ||
                    s.publicName === useBinding.source,
            );
            if (sym?.template && (sym.params?.length || sym.inputSchema)) {
                return { params: sym.params, inputSchema: sym.inputSchema };
            }
        }
        return undefined;
    }, [useBinding.source, definitions, paneCtx.contentDefinitions, paneCtx.parsedDocument, importSymbols]);

    // Close popover on outside click
    useEffect(() => {
        if (!showConfig) return;
        const handler = (e: MouseEvent) => {
            const t = e.target as HTMLElement;
            if (t.closest?.("[data-comboselect-portal]")) return;
            if (popoverRef.current && !popoverRef.current.contains(t)) {
                setShowConfig(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [showConfig, popoverRef, setShowConfig]);

    const hasAnyConfig = !!(useSourceConfig || templateSource);

    return (
        <div className="group/content-config relative">
            {children}
            {hasAnyConfig && (
                <button
                    type="button"
                    className={cn(
                        "absolute top-1 right-1 z-10 flex items-center justify-center",
                        "h-5 w-5 rounded-md border border-border/40 bg-background/80 backdrop-blur-sm",
                        "text-muted-foreground/60 hover:text-foreground hover:border-border hover:bg-background",
                        "transition-all shadow-sm",
                        showConfig
                            ? "opacity-100"
                            : "opacity-0 group-hover/content-config:opacity-100",
                    )}
                    title={t("contentBlockWrapper.configTitle")}
                    onClick={() => setShowConfig((v) => !v)}
                >
                    <Settings size={11} />
                </button>
            )}
            {showConfig && hasAnyConfig && (
                <div
                    ref={popoverRef}
                    className="absolute top-7 right-1 z-20 w-80 rounded-lg border border-border bg-background shadow-lg"
                >
                    <div className="p-2 space-y-2">
                        {useSourceConfig && (
                            <UseBindEditor
                                useBinding={useBinding}
                                definitions={definitions}
                                sourceConfig={useSourceConfig}
                                importSymbols={importSymbols}
                            />
                        )}
                        {templateSource && (
                            <UseWithEditor
                                useBinding={useBinding}
                                templateParams={templateSource.params ?? []}
                                inputSchema={templateSource.inputSchema}
                                definitions={definitions}
                            />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
